import type { AlbumJob } from '@/features/design/job-queue';
import type { Album } from '../api/album-service';

/**
 * Album rows of a design: the API list merged with in-flight queue jobs.
 *
 * The queue is the source of truth for live status and timing while an album
 * generates; the API row carries everything else (title, size, the wait copy
 * `wait_hint`). An in-flight job whose API row is already loaded therefore
 * becomes that API row with the job's status and timing, so the row keeps the
 * server copy. A job the API list has not caught up with yet stays a job.
 */
export function mergeAlbumsWithJobs(
  albums: Album[],
  jobs: AlbumJob[]
): Array<Album | AlbumJob> {
  const albumsById = new Map(albums.map((album) => [album.id, album]));
  const seenIds = new Set<string>();
  const result: Array<Album | AlbumJob> = [];

  jobs.forEach((job) => {
    seenIds.add(job.id);
    const apiAlbum = albumsById.get(job.id);
    if (!apiAlbum) {
      result.push(job);
    } else if (apiAlbum.status === 'completed') {
      result.push(apiAlbum);
    } else {
      result.push({
        ...apiAlbum,
        status: job.status as Album['status'],
        started_at: job.startedAt ?? apiAlbum.started_at,
        duration_predicted: job.durationPredicted ?? apiAlbum.duration_predicted,
      });
    }
  });

  albums.forEach((album) => {
    if (!seenIds.has(album.id)) result.push(album);
  });

  return result.sort((a, b) => {
    const dateA = 'createdAt' in a ? a.createdAt : a.created_at;
    const dateB = 'createdAt' in b ? b.createdAt : b.created_at;
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });
}
